// @flow
import React from 'react';
import styled from 'styled-components';
import type { LedgerAccount } from 'client-web-app/queries';
import formatCurrency from 'format-currency';
import { colors } from 'bench-style-constants';
import { Grid } from 'layout-core-components';
import { Tooltip, Icon } from 'client-web-app/ui';

import TransactionSidebarDataEntryText from './TransactionSidebarDataEntryText';
import TransactionSidebarDataEntryStatusContainer from './TransactionSidebarDataEntryStatusContainer';
import type { JournalEntry } from './types';

const TransactionSidebarTextTooltipWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  padding-top: 8px;
`;

const TransactionSidebarTooltipIconWrapper = styled.div`
  margin-left: 4px;
  display: inline-flex;
  vertical-align: text-bottom;
`;

const TransactionSidebarTooltipText = styled.div`
  width: 160px;
`;

type TransactionSidebarDataEntryRowProps = {
  clientCategorizationSelectedLedgerId: ?string,
  currentLedgerId: ?string,
  dataEntryLedgerAccountId: string,
  ledgerNameTestId: string,
  clientId: string,
  ledgerAccountsMap: Map<string, LedgerAccount>,
  transaction: JournalEntry,
  dataEntryAmountInCents: number,
};

export default function TransactionSidebarDataEntryRow({
  clientCategorizationSelectedLedgerId,
  currentLedgerId,
  dataEntryLedgerAccountId,
  dataEntryAmountInCents,
  ledgerNameTestId,
  clientId,
  ledgerAccountsMap,
  transaction,
}: TransactionSidebarDataEntryRowProps) {
  const dataEntryLedgerAccount =
    ledgerAccountsMap.has(dataEntryLedgerAccountId) &&
    ledgerAccountsMap.get(dataEntryLedgerAccountId);

  const isCurrentLedger = dataEntryLedgerAccount && dataEntryLedgerAccount.id === currentLedgerId;

  const isClientSelectedLedger =
    dataEntryLedgerAccount && dataEntryLedgerAccount.id === clientCategorizationSelectedLedgerId;

  const showTransactionStatus =
    transaction.dataEntries.length === 2 &&
    !isClientSelectedLedger &&
    dataEntryLedgerAccount &&
    !dataEntryLedgerAccount.role;

  const ledgerName =
    dataEntryLedgerAccount && dataEntryLedgerAccount.name
      ? dataEntryLedgerAccount.name
      : '[Unknown Ledger Account]';

  return (
    <Grid rowSpacing="medium" columnSpacing="medium" row="auto max-content">
      <TransactionSidebarTextTooltipWrapper>
        {showTransactionStatus ? (
          <TransactionSidebarDataEntryStatusContainer
            clientId={clientId}
            isCurrentLedger={!!isCurrentLedger}
            transactionDate={transaction.dateOfTransaction}
            ledgerName={ledgerName}
            ledgerNameTestId={ledgerNameTestId}
          />
        ) : (
          <TransactionSidebarDataEntryText
            data-test-id={ledgerNameTestId}
            isCurrentLedger={isCurrentLedger}
          >
            {ledgerName}
          </TransactionSidebarDataEntryText>
        )}
        {isClientSelectedLedger && (
          <Tooltip
            shouldUpdateOnOpen
            placement="top"
            tooltipContent={
              <TransactionSidebarTooltipText>
                You confirmed the category for this transaction.
              </TransactionSidebarTooltipText>
            }
          >
            <TransactionSidebarTooltipIconWrapper>
              <Icon size="18px" color={colors.water} icon="circleCheck" />
            </TransactionSidebarTooltipIconWrapper>
          </Tooltip>
        )}
      </TransactionSidebarTextTooltipWrapper>
      <TransactionSidebarDataEntryText isCurrentLedger={isCurrentLedger}>
        {formatCurrency(Math.abs(dataEntryAmountInCents) / 100)}
      </TransactionSidebarDataEntryText>
    </Grid>
  );
}
